import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { hasAccount } from '../account/hasAccount.js';

// SPA-route mapping for nav items. Anything not listed renders as <a href="#">
// (visibly inert). Maps the original ./*.html paths to the SPA route shape
// agreed in the handoff (e.g. /welsh/resources for welsh-resources.html).
const NAV_HREFS = {
  'Our free Welsh resources': '/welsh/resources',
  'Bedtime Welsh': '/welsh/bedtime',
  'Calon Lân': '/welsh/calon-lan',
  'National Anthem': '/welsh/anthem',
  'Mission': '/mission',
  'Methodology': '/methodology',
  'See it in action': '/intensive-learning-hub',
  'Our Team': '/about',
  "Aran's Notebook": '/arans-notebook',
  'Celebrity videos': '/celebrity-coaching',
  'Learner Stories': '/testimonials',
  'Pricing': '/pricing',
  'Contact': '/contact',
  // Community mega-menu
  'Forum':              '/forum',
  'Meetups & events':   '/forum?path=%2Fc%2Fwelsh%2Fmeetups-events%2F12',
  'WSP on Slack':       '/forum?path=%2Ft%2Fslack-welsh-speaking-practice-wsp-event-listings%2F25974',
  'Community overview': '/community',
  // Schools dropdown
  'Schools': '/schools1',
  'Tutors':  '/tutors',
};

// Anchor element that picks <Link> for SPA paths and plain <a> for hashes
// or external URLs. Keeps the rest of the component readable.
function NavAnchor({ href, style, className, children, onClick }) {
  if (!href || href === '#' || /^(https?:|mailto:|tel:|#)/.test(href)) {
    return <a href={href || '#'} style={style} className={className} onClick={onClick}>{children}</a>;
  }
  return <Link to={href} style={style} className={className} onClick={onClick}>{children}</Link>;
}

export function Header({ t, hideLocale, homeHref, isHomepage }) {
  const [open, setOpen] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e) => e.key === 'Escape' && setDrawerOpen(false);
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [drawerOpen]);

  const nav = [
    !hideLocale && { label: t.navWelshResources, groups: [
      { items: ['Our free Welsh resources'] },
      { heading: 'Songs', items: ['Calon Lân', 'National Anthem'] },
      { heading: 'Listen', items: ['Bedtime Welsh'] },
    ]},
    { label: t.navAboutUs, groups: [
      { heading: 'About', items: ['Mission', 'Methodology', 'See it in action', 'Our Team', "Aran's Notebook"] },
      { heading: 'Our learners', items: ['Celebrity videos', 'Learner Stories'] },
    ]},
    { label: t.navCommunity || 'Community', groups: [
      { heading: 'Community', items: ['Community overview', 'Forum'] },
      { heading: 'Where we meet', items: ['Meetups & events', 'WSP on Slack'] },
    ]},
    { label: t.navForSchools || 'Schools', groups: [
      { heading: 'For educators', items: ['Schools', 'Tutors'] },
    ]},
    { label: 'Pricing', href: NAV_HREFS.Pricing },
    { label: t.navContact, href: NAV_HREFS.Contact },
  ].filter(Boolean);

  const hasMenu = n => (n.items && n.items.length) || (n.groups && n.groups.length);
  const homePath = homeHref || '/';
  // Read once per mount — the header remounts on route changes, so the
  // CTA picks up a freshly-set flag on the next navigation.
  const [knownUser] = useState(hasAccount);

  return (
    <header style={headerStyles.bar}>
      <div className="ssi-header__inner" style={headerStyles.inner}>
        {isHomepage
          ? <span style={headerStyles.logoLink} aria-label="SaySomethingin">
              <img src="/assets/logo.svg?v=2" alt="SaySomethingin" style={headerStyles.logo}/>
            </span>
          : <Link to={homePath} style={headerStyles.logoLink} aria-label="SaySomethingin">
              <img src="/assets/logo.svg?v=2" alt="SaySomethingin" style={headerStyles.logo}/>
            </Link>}
        <nav className="ssi-header__nav" style={headerStyles.nav}>
          {nav.map(n => (
            <div key={n.label}
                 style={headerStyles.navItem}
                 onMouseEnter={() => hasMenu(n) && setOpen(n.label)}
                 onMouseLeave={() => setOpen(null)}>
              {n.href
                ? <NavAnchor href={n.href} style={{...headerStyles.navLink, textDecoration:'none', ...(open===n.label?{color:'var(--brandRed)'}:{})}}>{n.label}</NavAnchor>
                : <span style={{...headerStyles.navLink, ...(open===n.label?{color:'var(--brandRed)'}:{})}}>{n.label}</span>}
              {hasMenu(n) && open === n.label && (
                <div style={headerStyles.dropdown}>
                  {n.groups
                    ? n.groups.map((g, gi) => (
                        <div key={g.heading || `g${gi}`} style={gi > 0 ? headerStyles.ddGroupSpaced : null}>
                          {g.heading && <div style={headerStyles.ddHeading}>{g.heading}</div>}
                          {g.items.map(i => <NavAnchor key={i} href={NAV_HREFS[i] || '#'} style={headerStyles.ddItem}>{i}</NavAnchor>)}
                        </div>
                      ))
                    : n.items.map(i => <NavAnchor key={i} href={NAV_HREFS[i] || '#'} style={headerStyles.ddItem}>{i}</NavAnchor>)}
                </div>
              )}
            </div>
          ))}
        </nav>
        <div className="ssi-header__actions" style={headerStyles.right}>
          {/* Lang switcher hidden site-wide until Welsh pages are ready & proofread. */}
          {false && !hideLocale && (
            <div style={headerStyles.lang}>
              <a href="https://www.saysomethingin.com/en" aria-current="page" style={headerStyles.langLinkActive}>English</a>
              <span style={headerStyles.langSep}>/</span>
              <a href="https://www.saysomethingin.com/cy/" style={headerStyles.langLink}>Cymraeg</a>
            </div>
          )}
          {/* Sales funnel first: new visitors go to /pay; recognised
            * account holders (see account/hasAccount.js) go straight to
            * the embedded app. */}
          <Link to={knownUser ? '/app' : '/pay'} className="btn btn--primary">
            {knownUser ? 'Open the app' : t.startLearning}
          </Link>
        </div>
        <button
          className="ssi-header__burger"
          aria-label="Open menu"
          aria-expanded={drawerOpen}
          onClick={() => setDrawerOpen(true)}
          type="button"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="4" y1="7" x2="20" y2="7"/>
            <line x1="4" y1="12" x2="20" y2="12"/>
            <line x1="4" y1="17" x2="20" y2="17"/>
          </svg>
        </button>
      </div>
      {drawerOpen && (
        <div className="ssi-header__drawer" role="dialog" aria-modal="true" aria-label="Menu">
          <div className="ssi-header__drawerTop">
            <img src="/assets/logo.svg?v=2" alt="SaySomethingin" style={{height:32, width:'auto', display:'block'}}/>
            <button
              aria-label="Close menu"
              onClick={() => setDrawerOpen(false)}
              type="button"
              style={{
                width:42, height:42, borderRadius:10, background:'transparent',
                border:'1px solid rgba(73,3,0,.18)', cursor:'pointer',
                color:'var(--brandRed)', display:'inline-flex',
                alignItems:'center', justifyContent:'center',
              }}>
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="6" y1="6" x2="18" y2="18"/>
                <line x1="6" y1="18" x2="18" y2="6"/>
              </svg>
            </button>
          </div>
          <nav>
            {nav.map(n => {
              const links = [];
              if (n.href) links.push({ label: n.label, href: n.href });
              else if (n.groups) n.groups.forEach(g => g.items.forEach(i => links.push({ label: i, href: NAV_HREFS[i] || '#' })));
              else if (n.items) n.items.forEach(i => links.push({ label: i, href: NAV_HREFS[i] || '#' }));
              return links.map(l => (
                <NavAnchor key={`${n.label}-${l.label}`} href={l.href} onClick={() => setDrawerOpen(false)}>
                  {l.label}
                </NavAnchor>
              ));
            })}
          </nav>
          <div className="ssi-header__drawerActions">
            <Link to={knownUser ? '/app' : '/pay'} className="btn btn--primary" onClick={() => setDrawerOpen(false)}>
              {knownUser ? 'Open the app' : t.startLearning}
            </Link>
            {false && !hideLocale && (
              <div style={{textAlign:'center', fontSize:13, color:'var(--grey)', marginTop:8}}>
                <a href="https://www.saysomethingin.com/en" style={{color:'var(--fg-1)', textDecoration:'none', fontWeight:500}}>English</a>
                <span style={{margin:'0 8px', opacity:.55}}>/</span>
                <a href="https://www.saysomethingin.com/cy/" style={{color:'var(--grey)', textDecoration:'none'}}>Cymraeg</a>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

const headerStyles = {
  bar: { background:'#fff', borderBottom:'1px solid var(--border)' },
  inner: { maxWidth:1440, margin:'0 auto', padding:'18px 32px', display:'flex', alignItems:'center', gap:28 },
  logoLink: { display:'inline-flex', alignItems:'center', color:'var(--brandRed)', textDecoration:'none' },
  logo: { height:36, width:'auto', display:'block' },
  nav: { display:'flex', gap:22, marginLeft:24, flex:1 },
  navItem: { position:'relative', padding:'8px 0', cursor:'pointer' },
  navLink: { fontSize:14, fontWeight:500, color:'var(--fg-1)', transition:'color 160ms' },
  chev: { fontSize:12, opacity:.6, marginLeft:2 },
  dropdown: { position:'absolute', top:'100%', left:-14, background:'#fff', minWidth:220, padding:'8px 0', borderRadius:12, boxShadow:'var(--shadow-md)', border:'1px solid var(--border)', zIndex:10 },
  ddHeading: { padding:'8px 18px 4px', fontSize:11, fontWeight:600, letterSpacing:'.12em', textTransform:'uppercase', color:'var(--grey)' },
  ddGroupSpaced: { borderTop:'1px solid var(--border)', marginTop:6, paddingTop:2 },
  ddItem: { display:'block', padding:'10px 18px', fontSize:14, color:'var(--fg-1)', textDecoration:'none', cursor:'pointer' },
  right: { display:'flex', alignItems:'center', gap:18 },
  lang: { display:'inline-flex', alignItems:'baseline', gap:6, fontSize:13 },
  langLinkActive: { color:'var(--fg-1)', textDecoration:'none', fontWeight:500 },
  langLink: { color:'var(--grey)', textDecoration:'none', opacity:.8 },
  langSep: { color:'var(--grey)', opacity:.55 },
  login: { fontSize:14, fontWeight:500, color:'var(--fg-1)', cursor:'pointer' },
};
